import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import useAttentiveEvents, {
  AttentiveProductProps,
} from "@hooks/useAttentiveEvents"
import useElevarEvents, {
  ELEVAR_VIEW_ITEM,
  ElevarProductProps,
} from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import useMaestraEvents from "@hooks/useMaestraEvents"
import { getMarketAmount } from "@utils/pricing"
import _ from "lodash-es"
import React from "react"

type Args = {
  availabilityData: AvailabilityData
  currentVariant?: any
  customerCountryCode?: string
  customerEmail?: string
  inventoryLoaded: boolean
  mode?: "PDP" | "SOPDP"
  multipleDevices?: string // only relevant for PDP
  productData: ProductData
}

type AvailabilityData = { isAvailable?: boolean; quantity?: number } | null
type ProductData = any

export function usePdpAnalytics({
  availabilityData,
  currentVariant,
  customerCountryCode = "US",
  customerEmail,
  inventoryLoaded,
  mode = "PDP",
  multipleDevices,
  productData,
}: Args) {
  const { onViewProductDetails } = useEnhancedEcommerceEvents()
  const { elevarOnViewItem } = useElevarEvents()
  const { maestraOnViewProduct } = useMaestraEvents()
  const { attentiveOnViewItem } = useAttentiveEvents()

  const hasAvailabilityData =
    availabilityData != null &&
    typeof availabilityData.isAvailable === "boolean"

  const deviceForGTM = React.useMemo(() => {
    if (mode === "SOPDP") {
      return _.get(productData, "device.name")
    }
    // PDP
    return multipleDevices
      ? multipleDevices.replace("#", ", ")
      : _.get(productData, "device.name")
  }, [mode, multipleDevices, productData])

  const deviceForVariantPayloads = React.useMemo(() => {
    if (mode === "SOPDP") {
      // Prefer variant.deviceName, then variant.device, then product.device.name
      return (
        currentVariant?.deviceName ||
        currentVariant?.device ||
        _.get(productData, "device.name")
      )
    }
    // PDP
    return multipleDevices
      ? multipleDevices.replace("#", ", ")
      : _.get(productData, "device.name")
  }, [mode, multipleDevices, currentVariant, productData])

  // GTM view event (once when currentVariant resolves)
  React.useEffect(() => {
    if (!currentVariant) return
    const gtmProduct: GTMProductProps = {
      brand: _.get(productData, "brand.name"),
      device: deviceForGTM,
      deviceType: _.get(productData, "device.type.name"),
      list: "Product Details",
      price: getMarketAmount(currentVariant, customerCountryCode, "price"),
      productId: _.get(productData, "shopifyProduct.shopifyProductId"),
      productName: _.get(productData, "productName"),
      sku: currentVariant?.sku,
      variantName: currentVariant?.name,
    }
    onViewProductDetails(gtmProduct)
  }, [
    !!currentVariant,
    deviceForGTM,
    customerCountryCode,
    onViewProductDetails,
    productData,
  ])

  // Elevar view event (once per variant resolve)
  React.useEffect(() => {
    if (!(inventoryLoaded && hasAvailabilityData)) return
    if (!currentVariant?.variantId) return

    const selected = ((): Record<string, string> => {
      try {
        const raw = localStorage.getItem(SELECTED_PRODUCTS_KEY)
        return raw ? JSON.parse(raw) : {}
      } catch {
        return {}
      }
    })()

    const list =
      selected[_.get(productData, "shopifyProduct.shopifyProductId")] ||
      undefined
    const imageSrc = _.get(currentVariant, "images.0", "")
    const [cleanedSrc] = (imageSrc || "").split("?")
    const inventory = availabilityData?.quantity || 0

    const elevarProduct: ElevarProductProps = {
      brand: "Urban Armor Gear",
      compareAtPrice:
        getMarketAmount(
          currentVariant,
          customerCountryCode,
          "compareAtPrice"
        ) || "0.00",
      device: deviceForVariantPayloads,
      deviceType: _.get(productData, "device.type.name"),
      image: `https:${cleanedSrc}`,
      inventory,
      list,
      price: getMarketAmount(currentVariant, customerCountryCode, "price"),
      productId: _.get(productData, "shopifyProduct.shopifyProductId"),
      productName: _.get(productData, "productName"),
      sku: currentVariant?.sku,
      variantId: currentVariant?.variantId,
      variantName: currentVariant?.name,
    }

    const sendViewItemOnce = (product: ElevarProductProps, list?: string) => {
      const dataLayer = _.get(window, "dataLayer", [])
      const i = _.findLastIndex(
        dataLayer,
        ({ event }) => event === ELEVAR_VIEW_ITEM
      )
      const lastSku = _.get(dataLayer, `${i}.ecommerce.detail.products.0.sku`)
      if (lastSku !== product.sku) elevarOnViewItem(product, list as any)
    }

    sendViewItemOnce(elevarProduct, list)
  }, [
    inventoryLoaded,
    hasAvailabilityData,
    currentVariant,
    productData,
    availabilityData,
    customerCountryCode,
    deviceForVariantPayloads,
    elevarOnViewItem,
  ])

  // Maestra view event (once per variant resolve)
  React.useEffect(() => {
    if (!inventoryLoaded) return
    if (!currentVariant?.variantId) return

    maestraOnViewProduct({
      product: _.get(productData, "shopifyProduct.shopifyProductId"),
      user: { email: customerEmail || "" },
    })
  }, [
    inventoryLoaded,
    currentVariant,
    productData,
    customerEmail,
    maestraOnViewProduct,
  ])

  // Attentive view event (fire once)
  const attentiveSentRef = React.useRef(false)
  React.useEffect(() => {
    if (attentiveSentRef.current) return
    if (!(inventoryLoaded && hasAvailabilityData)) return
    if (!currentVariant?.variantId) return

    const imageSrc = _.get(currentVariant, "images.0", "")
    const [cleanedSrc] = (imageSrc || "").split("?")
    const attentiveProduct: AttentiveProductProps = {
      brand: "Urban Armor Gear",
      device: deviceForVariantPayloads,
      deviceType: _.get(productData, "device.type.name"),
      image: `https:${cleanedSrc}`,
      price: getMarketAmount(currentVariant, customerCountryCode, "price"),
      productId: _.get(productData, "shopifyProduct.shopifyProductId"),
      productName: _.get(productData, "productName"),
      sku: currentVariant?.sku,
      variantId: currentVariant?.variantId,
      variantName: currentVariant?.name,
    }
    attentiveOnViewItem(attentiveProduct)
    attentiveSentRef.current = true
  }, [
    inventoryLoaded,
    hasAvailabilityData,
    currentVariant,
    productData,
    deviceForVariantPayloads,
    customerCountryCode,
    attentiveOnViewItem,
  ])
}
